import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { PASSIONS } from "../constants";
import { type User } from "../types";
import { motion, AnimatePresence, type Variants } from "framer-motion";
import { ArrowLeftIcon } from "../components/icons/FeatherIcons";
import {
  generatePersonaTitle,
  generateAlternativePersonas,
} from "../utils/personaUtils";
import { useActivityActions } from "../contexts/ActivityContext";
import { collection, query, where, getDocs } from "firebase/firestore";
import { db } from "../services/firebase";
import { usePageTracking } from "../hooks/usePageTracking";
import { getNames } from "country-list";
import { Capacitor } from "@capacitor/core";
import { DatetimePicker } from "@capawesome-team/capacitor-datetime-picker";

import { crashlytics } from "../services/firebase";
import { CommonFunctions } from "../utils/commonUtils";
const TOTAL_STEPS = 3;

const getErrorMessage = (error: unknown): string => {
  if (error instanceof Error) {
    return error.message;
  }
  return String(error);
};

const StepIndicator: React.FC<{ currentStep: number }> = ({ currentStep }) => (
  <div className="w-full bg-gray-200 rounded-full h-1.5">
    <motion.div
      className="bg-primary h-1.5 rounded-full"
      animate={{ width: `${(currentStep / TOTAL_STEPS) * 100}%` }}
      transition={{ type: "spring", stiffness: 100, damping: 20 }}
    />
  </div>
);

const countries: string[] = getNames(); // returns array of country names

const BuilderHeader: React.FC<{ onBack: () => void; currentStep: number }> = ({
  onBack,
  currentStep,
}) => (
  <header className="px-4 pb-4 flex justify-between items-center">
    <button
      onClick={onBack}
      className={`transition-opacity ${currentStep === 1 ? "opacity-0 pointer-events-none" : "opacity-100"}`}
    >
      <ArrowLeftIcon className="w-6 h-6 text-gray-500" />
    </button>
    <div className="w-6"></div>
  </header>
);

const ProfileBuilderPage: React.FC = () => {
  usePageTracking("ProfileBuilder", "ProfileBuilderPage");

  const isNative = Capacitor.isNativePlatform();

  const { currentUser, updateUser, markOnboardingComplete } = useAuth();
  const { createUserJoinedAuditLog } = useActivityActions();
  const navigate = useNavigate();

  const [currentStep, setCurrentStep] = useState(1);
  const [formData, setFormData] = useState<Partial<User>>({
    username: "",
    personaTitle: "",
    dateOfBirth: "",
    gender: "undisclosed",
    bio: "",
    passions: [],
    location: "",
  });
  const [isLoading, setIsLoading] = useState(false);
  const [usernameError, setUsernameError] = useState<string>("");
  const [dateOfBirthError, setDateOfBirthError] = useState<string>("");
  const [genderError, setGenderError] = useState<string>("");

  useEffect(() => {
    if (currentUser) {
      setFormData((prev) => ({ ...prev, ...currentUser }));
    }
  }, [currentUser]);

  const handleNext = () =>
    currentStep < TOTAL_STEPS && setCurrentStep((s) => s + 1);
  const handleBack = () => currentStep > 1 && setCurrentStep((s) => s - 1);

  const validateAge = (): boolean => {
    if (!formData.dateOfBirth) {
      setDateOfBirthError("Please select your date of birth");
      return false;
    }
    let age = CommonFunctions.calculateAge(formData.dateOfBirth);
    if (age < 12) {
      setDateOfBirthError("You must be at least 12 years old to use this app");
      return false;
    }
    setDateOfBirthError("");
    return true;
  };

  const checkUsernameAvailability = async (): Promise<boolean> => {
    if (!currentUser || !formData.username?.trim()) return false;

    // If username hasn't changed, allow proceeding
    if (formData.username === currentUser.username) return true;

    try {
      const firestore = db;
      if (!firestore) {
        return true;
      }
      const usersRef = collection(firestore, "users");
      const q = query(usersRef, where("username", "==", formData.username));
      const querySnapshot = await getDocs(q);

      if (!querySnapshot.empty) {
        setUsernameError(
          "This username is already taken. Please try a different one.",
        );
        return false;
      }

      setUsernameError("");
      return true;
    } catch (error) {
      crashlytics.logError(
        `Error checking username availability: ${getErrorMessage(error)}`,
        "ProfileBuilderPage.tsx",
        error,
      );
      return true; // Allow proceeding if check fails
    }
  };

  const handleNextWithValidation = async () => {
    // Validate username and age on step 1 before proceeding
    if (currentStep === 1) {
      setIsLoading(true);
      // Validate age first
      const isAgeValid = validateAge();
      if (!isAgeValid) {
        setIsLoading(false);
        return;
      }
      // Validate gender
      if (!formData.gender || formData.gender === "undisclosed") {
        setGenderError("Please select your gender");
        setIsLoading(false);
        return;
      } else {
        setGenderError("");
      }
      // Then validate username
      const isUsernameValid = await checkUsernameAvailability();
      setIsLoading(false);
      if (!isUsernameValid) return; // Don't proceed if username is taken
    }

    handleNext();
  };

  const handleFinish = async () => {
    if (!currentUser) return;
    setIsLoading(true);
    setUsernameError(""); // Clear any previous errors

    try {
      const profileImageUrl = currentUser.profileImageUrl;
      await updateUser({ ...formData, profileImageUrl });
      // Don't mark onboarding complete here - let onboarding page do it

      setIsLoading(false);
      navigate("/onboarding", { replace: true });
    } catch (error) {
      crashlytics.logError(
        `Error finishing profile setup: ${getErrorMessage(error)}`,
        "ProfileBuilderPage.tsx",
        error,
      );
      const errorMessage =
        error instanceof Error ? error.message : "An error occurred";

      // Check if it's a username error
      if (errorMessage.includes("username") && errorMessage.includes("taken")) {
        setUsernameError(errorMessage);
        setCurrentStep(1); // Go back to step 1 where username is
      }

      setIsLoading(false);
    }
  };

  const handlePassionSelect = (passion: string) => {
    setFormData((prev) => {
      const currentPassions = prev.passions || [];
      let newPassions;

      if (currentPassions.includes(passion)) {
        newPassions = currentPassions.filter((p) => p !== passion);
      } else if (currentPassions.length >= 7) {
        return prev;
      } else {
        newPassions = [...currentPassions, passion];
      }

      // Auto-generate persona title based on selected passions
      const newPersonaTitle = generatePersonaTitle(newPassions);

      return {
        ...prev,
        passions: newPassions,
        personaTitle: newPersonaTitle,
      };
    });
  };

  const handleRefreshPersonaTitle = () => {
    const currentPassions = formData.passions || [];
    let newTitle;
    let attempts = 0;
    const maxAttempts = 10;

    // Keep generating until we get a different title or reach max attempts
    do {
      newTitle = generatePersonaTitle(currentPassions);
      attempts++;
    } while (newTitle === formData.personaTitle && attempts < maxAttempts);

    // If we still have the same title after max attempts, try alternatives
    if (newTitle === formData.personaTitle) {
      const alternatives = generateAlternativePersonas(
        currentPassions,
        formData.personaTitle,
      );
      if (alternatives.length > 0) {
        newTitle =
          alternatives[Math.floor(Math.random() * alternatives.length)];
      }
    }

    setFormData({ ...formData, personaTitle: newTitle });
  };

  const contentVariants: Variants = {
    enter: (direction: number) => ({ opacity: 0, x: direction > 0 ? 50 : -50 }),
    center: {
      opacity: 1,
      x: 0,
      transition: { type: "spring", stiffness: 250, damping: 30 },
    },
    exit: (direction: number) => ({
      opacity: 0,
      x: direction < 0 ? 50 : -50,
      transition: { duration: 0.2 },
    }),
  };

  const handleDateClick = async () => {
    if (isNative) {
      try {
        const result = await DatetimePicker.present({
          // Set the mode (date, time, or datetime)
          mode: "date",
          androidDatePickerMode: "spinner",
          // The format property defines how the selected date is displayed to the user
          format: "yyyy-MM-dd",
          // The 'value' must adhere to the ISO 8601 format (YYYY-MM-DDTHH:mmZ) internally
          value: formData.dateOfBirth || new Date().toISOString().split("T")[0],
          // You can also set locales to match regional preferences
          locale: "en-SG",
        });
        if (result.value) {
          setFormData({ ...formData, dateOfBirth: result.value.split("T")[0] });
          setDateOfBirthError(""); // Clear error when user selects date
        }
      } catch (e) {
        // handle cancel or error
      }
    }
  };

  const [direction, setDirection] = useState(1);

  const paginate = (newDirection: number) => {
    setDirection(newDirection);
    if (newDirection > 0) handleNext();
    else handleBack();
  };

  const Step1_BasicInfo = (
    <div className="p-4 md:max-w-lg md:mx-auto">
      <h1 className="text-2xl font-bold text-gray-900">Let's get started</h1>
      <p className="text-gray-500 mt-1 mb-6">
        This information will be shown on your public profile.
      </p>
      <div className="space-y-6">
        <div>
          <label className="text-sm font-semibold text-gray-600">
            Username
          </label>
          <p className="text-xs text-gray-500 mb-1">
            This is your unique identifier - others will see this on your
            profile
          </p>
          <input
            type="text"
            value={formData.username || ""}
            onChange={(e) => {
              setFormData({ ...formData, username: e.target.value });
              setUsernameError(""); // Clear error when user types
            }}
            placeholder="e.g., john_doe"
            className={`w-full mt-1 bg-gray-100 border-gray-300 border rounded-lg p-3 focus:outline-none focus:ring-2 ${usernameError ? "border-red-500 focus:ring-red-500" : "focus:ring-primary"}`}
          />
          {usernameError && (
            <p className="text-xs text-red-600 mt-1 font-medium">
              ⚠️ {usernameError}
            </p>
          )}
        </div>
        <div>
          <label className="text-sm font-semibold text-gray-600">
            Your Date of Birth
          </label>
          {isNative ? (
            <>
              <input
                type="text"
                value={formData.dateOfBirth || ""}
                readOnly
                onClick={handleDateClick}
                placeholder="Select date"
                className={`w-full text-base text-gray-900 focus:outline-none bg-transparent cursor-pointer ${dateOfBirthError ? "text-red-600" : ""}`}
              />
              {dateOfBirthError && (
                <p className="text-xs text-red-600 mt-1 font-medium">
                  ⚠️ {dateOfBirthError}
                </p>
              )}
            </>
          ) : (
            <>
              <input
                type="date"
                value={formData.dateOfBirth || ""}
                onChange={(e) => {
                  setFormData({ ...formData, dateOfBirth: e.target.value });
                  setDateOfBirthError(""); // Clear error when user changes date
                }}
                className={`w-full text-base text-gray-900 focus:outline-none bg-transparent block
                                            [&::-webkit-date-and-time-value]:text-left
                                            [&::-webkit-date-and-time-value]:m-0 ${dateOfBirthError ? "text-red-600" : ""}`}
              />
              {dateOfBirthError && (
                <p className="text-xs text-red-600 mt-1 font-medium">
                  ⚠️ {dateOfBirthError}
                </p>
              )}
            </>
          )}
        </div>
        <div>
          <label className="text-sm font-semibold text-gray-600">
            Your Location
          </label>
          <select
            value={formData.location || "Singapore"}
            onChange={(e) =>
              setFormData({ ...formData, location: e.target.value })
            }
            className="w-full mt-1 bg-gray-100 border-gray-300 border rounded-lg p-3 focus:outline-none focus:ring-2 focus:ring-primary"
          >
            <option value="">Select a country</option>
            {countries.map((country) => (
              <option key={country} value={country}>
                {country}
              </option>
            ))}
          </select>
          <p className="text-xs text-gray-500 mt-1">e.g. Singapore</p>
        </div>
        <div>
          <label className="text-sm font-semibold text-gray-600 mb-2 block">
            Your Gender
          </label>
          <div className="flex space-x-2">
            {(["male", "female", "other"] as const).map((gender) => (
              <button
                key={gender}
                type="button"
                onClick={() => setFormData({ ...formData, gender })}
                className={`flex-1 p-3 rounded-lg border-2 font-semibold transition-colors ${formData.gender === gender ? "bg-primary/10 border-primary text-primary" : "bg-gray-100 border-transparent text-gray-700"}`}
              >
                {gender.charAt(0).toUpperCase() + gender.slice(1)}
              </button>
            ))}
          </div>
          {genderError && (
            <p className="text-xs text-red-600 mt-1 font-medium">
              ⚠️ {genderError}
            </p>
          )}
        </div>
      </div>
    </div>
  );

  const bioSuggestions = [
    "I'm an avid reader, always looking for the next great book.",
    "Movie buff who loves everything from indie films to blockbusters.",
    "Fitness enthusiast who's always up for a challenge.",
    "Outdoor adventurer seeking new trails and scenic views.",
  ];

  const Step2_Introduce = (
    <div className="p-4 md:max-w-lg md:mx-auto">
      <h1 className="text-2xl font-bold text-gray-900">Introduce yourself</h1>
      <p className="text-gray-500 mt-1 mb-6">Tell us something about you!</p>
      <textarea
        value={formData.bio || ""}
        onChange={(e) => setFormData({ ...formData, bio: e.target.value })}
        rows={6}
        maxLength={200}
        className="w-full bg-gray-100 border-gray-300 border rounded-lg p-3 focus:outline-none focus:ring-2 focus:ring-primary"
      />
      <p className="text-right text-sm text-gray-400 mt-1">
        {(formData.bio || "").length}/200
      </p>
      <div className="mt-4">
        <p className="text-sm font-semibold text-gray-600 mb-2">
          Need some inspiration?
        </p>
        <div className="flex flex-wrap gap-2">
          {bioSuggestions.map((suggestion, i) => (
            <button
              key={i}
              type="button"
              onClick={() => setFormData({ ...formData, bio: suggestion })}
              className="text-left text-sm bg-gray-100 text-gray-600 p-2 rounded-md hover:bg-gray-200 transition-colors"
            >
              "{suggestion}"
            </button>
          ))}
        </div>
      </div>
    </div>
  );

  const Step3_Passions = (
    <div className="p-4 md:max-w-lg md:mx-auto">
      {/* Generated Persona Title Section */}
      {formData.passions && formData.passions.length > 0 && (
        <div className="mb-8 p-4 bg-gradient-to-br from-purple-50 to-blue-50 rounded-xl border border-purple-100">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-lg font-bold text-gray-900">Your Persona</h3>
            <div className="flex items-center space-x-2">
              <motion.div
                key={formData.personaTitle}
                initial={{ scale: 0.8, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                className="text-xs text-purple-600 font-semibold bg-purple-100 px-2 py-1 rounded-full"
              >
                ✨ Auto-generated
              </motion.div>
            </div>
          </div>

          <div className="space-y-3">
            {/* Current Persona Title */}
            <div className="flex items-center space-x-3">
              <div className="flex-1">
                <motion.input
                  key={formData.personaTitle}
                  initial={{ y: -10, opacity: 0 }}
                  animate={{ y: 0, opacity: 1 }}
                  type="text"
                  value={formData.personaTitle || ""}
                  onChange={(e) =>
                    setFormData({ ...formData, personaTitle: e.target.value })
                  }
                  placeholder="Your persona title..."
                  className="w-full bg-white border-2 border-purple-200 rounded-lg p-3 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent font-medium text-lg"
                />
              </div>
              <button
                type="button"
                onClick={handleRefreshPersonaTitle}
                className="px-4 py-3 bg-purple-100 text-purple-700 rounded-lg hover:bg-purple-200 transition-colors font-medium text-sm"
              >
                🎲 Refresh
              </button>
            </div>

            {/* Alternative Suggestions */}
            {(() => {
              const alternatives = generateAlternativePersonas(
                formData.passions || [],
                formData.personaTitle,
              );
              return (
                alternatives.length > 0 && (
                  <div>
                    <p className="text-xs text-gray-500 mb-2">
                      Or try one of these:
                    </p>
                    <div className="flex flex-wrap gap-2">
                      {alternatives.map((alternative, index) => (
                        <motion.button
                          key={alternative}
                          initial={{ opacity: 0, scale: 0.9 }}
                          animate={{ opacity: 1, scale: 1 }}
                          transition={{ delay: index * 0.1 }}
                          type="button"
                          onClick={() =>
                            setFormData({
                              ...formData,
                              personaTitle: alternative,
                            })
                          }
                          className="text-xs bg-white border border-purple-200 text-purple-700 px-3 py-1.5 rounded-full hover:bg-purple-50 hover:border-purple-300 transition-colors font-medium"
                        >
                          {alternative}
                        </motion.button>
                      ))}
                    </div>
                  </div>
                )
              );
            })()}
          </div>

          <p className="text-xs text-gray-500 mt-3">
            💡 This updates automatically as you pick your passions. You can
            edit it anytime!
          </p>
        </div>
      )}

      <h1 className="text-2xl font-bold text-gray-900">Your Passions</h1>
      <p className="text-gray-500 mt-1 mb-2">
        Let everyone know what you're passionate about. Pick at least 3 passions
        to continue (up to 7 max).
      </p>
      <p className="text-sm font-semibold mb-6">
        <span
          className={`${(formData.passions?.length || 0) >= 3 ? "text-green-600" : "text-gray-400"}`}
        >
          Selected: {formData.passions?.length || 0}/7 (minimum 3 required)
        </span>
      </p>
      <div className="flex flex-wrap gap-2">
        {PASSIONS.map((passion) => {
          const isSelected = formData.passions?.includes(passion);
          return (
            <button
              key={passion}
              type="button"
              onClick={() => handlePassionSelect(passion)}
              className={`px-4 py-2 rounded-full border-2 font-semibold transition-all duration-200 ${isSelected ? "bg-primary/10 border-primary text-primary" : "bg-gray-100 border-transparent text-gray-700 hover:border-gray-300"}`}
            >
              {passion}
            </button>
          );
        })}
      </div>
    </div>
  );

  const isNextDisabled = () => {
    if (
      currentStep === 1 &&
      (!formData.username?.trim() || !formData.location?.trim())
    )
      return true;
    if (
      currentStep === 3 &&
      ((formData.passions?.length || 0) < 3 || !formData.personaTitle?.trim())
    )
      return true;
    return false;
  };

  // Get the current step content for the 3-step flow
  const getCurrentStepContent = () => {
    if (currentStep === 1) return Step1_BasicInfo;
    if (currentStep === 2) return Step2_Introduce;
    if (currentStep === 3) return Step3_Passions;

    return Step1_BasicInfo;
  };

  const getButtonText = () => {
    if (currentStep === 3) {
      // Passions step - now the final step
      const count = formData.passions?.length || 0;
      return `Finish Profile (${count}/7)`;
    }

    return "Continue";
  };

  return (
    <div className="flex flex-col h-full bg-white">
      <div
        className="px-4 pt-4 pb-2"
        style={{ paddingTop: "max(1.5rem, env(safe-area-inset-top))" }}
      >
        <StepIndicator currentStep={currentStep} />
      </div>
      <BuilderHeader onBack={() => paginate(-1)} currentStep={currentStep} />
      <main className="flex-1 overflow-y-auto hide-scrollbar relative">
        <AnimatePresence initial={false} custom={direction}>
          <motion.div
            key={currentStep}
            custom={direction}
            variants={contentVariants}
            initial="enter"
            animate="center"
            exit="exit"
            className="w-full h-full"
          >
            {getCurrentStepContent()}
          </motion.div>
        </AnimatePresence>
      </main>
      <footer className="p-4 pb-safe-or-4 border-t border-gray-200">
        <button
          onClick={
            currentStep < TOTAL_STEPS ? handleNextWithValidation : handleFinish
          }
          disabled={isNextDisabled() || isLoading}
          className="w-full bg-primary text-white py-3 rounded-lg font-semibold disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {isLoading ? "Checking..." : getButtonText()}
        </button>
      </footer>
    </div>
  );
};

export default ProfileBuilderPage;
